// src/authUtils.js

  const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE
const TOKEN_KEY = 'auth_token';

/**
 * STEP 1: POST /api/register
 * Creates a “pending” user, sends signup OTP to the given email.
 * Returns { success, message, email }.
 */
export async function registerStep1(email, password, fullName, phone) {
  try {
    const res = await fetch(`${REACT_APP_API_BASE}/api/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, fullName, phone })
    });
    const data = await res.json();
    if (res.ok && data.success) {
      // Backend does NOT yet return a JWT here—only a message and email.
      return { success: true, message: data.message, email: data.email };
    } else {
      return { success: false, message: data.message || 'Registration failed.' };
    }
  } catch (err) {
    console.error('registerStep1 error:', err);
    return { success: false, message: 'Network error.' };
  }
}

/**
 * STEP 2: POST /api/verify-signup-otp
 * Verify the signup OTP and (if correct) receive { user, token }.
 */
export async function verifySignupOtp(email, otp) {
  try {
    const res = await fetch(`${REACT_APP_API_BASE}/api/verify-signup-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp })
    });
    const data = await res.json();
    if (res.ok && data.success) {
      localStorage.setItem(TOKEN_KEY, data.token);
      return { success: true, user: data.user, token: data.token };
    } else {
      return { success: false, message: data.message || 'OTP verification failed.' };
    }
  } catch (err) {
    console.error('verifySignupOtp error:', err);
    return { success: false, message: 'Network error.' };
  }
}

/**
 * STEP 1: POST /api/login
 * Verify email+password, send login‐OTP to email.
 * Returns { success, message, email }.
 */
export async function loginStep1(email, password) {
  try {
    const res = await fetch(`${REACT_APP_API_BASE}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (res.ok && data.success) {
      // Backend does NOT yet return a JWT—only a message and email.
      return { success: true, message: data.message, email: data.email };
    } else {
      return { success: false, message: data.message || 'Login failed.' };
    }
  } catch (err) {
    console.error('loginStep1 error:', err);
    return { success: false, message: 'Network error.' };
  }
}

/**
 * STEP 2: POST /api/verify-login-otp
 * Verify the login OTP and (if correct) receive { user, token }.
 */
export async function verifyLoginOtp(email, otp) {
  try {
    const res = await fetch(`${REACT_APP_API_BASE}/api/verify-login-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp })
    });
    const data = await res.json();
    if (res.ok && data.success) {
      localStorage.setItem(TOKEN_KEY, data.token);
      return { success: true, user: data.user, token: data.token };
    } else {
      return { success: false, message: data.message || 'OTP verification failed.' };
    }
  } catch (err) {
    console.error('verifyLoginOtp error:', err);
    return { success: false, message: 'Network error.' };
  }
}

/**
 * GET /api/current-user
 * If a valid JWT exists in localStorage, returns the user object; otherwise null.
 */
export async function getCurrentUser() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  try {
    const res = await fetch(`${REACT_APP_API_BASE}/api/current-user`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const data = await res.json();
    if (res.ok && data.success) {
      return data.user;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
  } catch (err) {
    console.error('getCurrentUser error:', err);
    return null;
  }
}

/**
 * Remove JWT from localStorage.
 */
export function logoutUser() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Listens for “storage” events so that if another tab logs out,
 * we can respond accordingly. Calls onAuthChange(false) if token disappears.
 */
export function setupAuthListeners(onAuthChange) {
  function handleStorageEvent(e) {
    if (e.key === TOKEN_KEY) {
      if (!localStorage.getItem(TOKEN_KEY)) {
        onAuthChange(false);
      }
    }
  }
  window.addEventListener('storage', handleStorageEvent);
  return () => {
    window.removeEventListener('storage', handleStorageEvent);
  };
}
